import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AuthLoadingScreen } from "@/lib/components/auth/auth-loading-screen";
import { AuthStatus, useAuthStore } from "@/lib/store/auth-enhanced";

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

export function AuthGuard({ children, requireAuth = true }: AuthGuardProps) {
  const { isAuthenticated, status, initialize } = useAuthStore();
  const location = useLocation();

  // Initialize auth on mount — validates token before rendering children
  useEffect(() => {
    if (status === AuthStatus.UNINITIALIZED) {
      initialize();
    }
  }, [initialize, status]);

  // Wait for auth initialization before making any routing decisions
  if (status !== AuthStatus.READY) {
    return <AuthLoadingScreen />;
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!requireAuth && isAuthenticated) {
    const from = location.state?.from?.pathname || "/";
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}
